import type { GroundworksData } from "../types/tender.types";
import { RetainingWallsBuildUpSpecification } from "../api/retainingWallsBuildUp";
import { computeSoilManagementDerivedCutFillValues } from "./groundworksBoqSummaryComputer";
import {
  OSMA_FOUL_COMBINED_FIELDS,
  OSMA_STORM_COMBINED_FIELDS,
  POLYPIPE_FOUL_COMBINED_FIELDS,
  POLYPIPE_STORM_COMBINED_FIELDS,
} from "./drainageReferenceMapping";
import { alignPlotDrivesSectionBoundaries, sanitizePlotDrivesConfig, PLOT_DRIVES_COLUMN_MAPPING } from "./plotDrivesColumnMapping";
import { isResolvablePlotDrivesEvaluateValue } from "./plotDrivesItemLibraryIntegration";

export interface ConfigureHeadersValidationError {
  /** Human-readable section this violation belongs to, e.g. "Cut & Fill — Site Clearance". */
  section: string;
  /** What's wrong and how to fix it, e.g. "Strip topsoil has a quantity entered but its depth isn't configured." */
  message: string;
}

export interface ConfigureHeadersValidationResult {
  isValid: boolean;
  errors: ConfigureHeadersValidationError[];
}

const isRealRow = (row: Record<string, unknown>): boolean =>
  row.key !== "totals" && row.plotNo !== "TOTALS" && row.plotNo !== "Total";

const isBlank = (value: unknown): boolean => value == null || String(value).trim() === "";

const sumField = (rows: Record<string, any>[], field: string): number =>
  rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);

interface GatedField {
  /** Key in the config Record<string,string> (dimensionValues/prefixValues/columnHeaders). */
  configKey: string;
  /** Field name on each take-off table row holding this field's quantity. */
  tableField: string;
  /** Human label for the error message, e.g. "Strip topsoil". */
  label: string;
  /**
   * Per-field override for the error's bold `section` label, used whenever a
   * domain's fields don't all live on the same tab (e.g. Plot Drives' 6
   * drive-type × 2 stage tabs). Falls back to the `section` argument passed
   * to `checkGatedFields` when omitted.
   */
  section?: string;
}

/**
 * A single Configure Headers violation, keyed by the take-off `fieldName` its
 * domain's Bill Summary row identifies itself by — lets a Bill Summary
 * component look up `violationsByField.get(item.fieldName)` to show a warning
 * icon directly on the affected line item, the same data the Send for Review
 * gate on /groundworks/review uses.
 */
export interface FieldConfigureHeadersViolation {
  fieldName: string;
  section: string;
  message: string;
}

/**
 * Generic check for the common shape shared by every domain below: a
 * take-off table of rows, and a flat `Record<string,string>` of Configure
 * Headers values keyed by field — flag any field whose table quantity sums
 * to more than 0 while its config value is blank/unset. `violationsByField`,
 * when passed, also records each violation keyed by its `tableField` (which
 * equals the Bill Summary row's own `fieldName` for every domain that uses
 * this generic check directly).
 */
function checkGatedFields(
  rows: Record<string, any>[],
  configValues: Record<string, string> | undefined,
  fields: readonly GatedField[],
  section: string,
  errors: ConfigureHeadersValidationError[],
  violationsByField?: Map<string, FieldConfigureHeadersViolation>,
): void {
  for (const field of fields) {
    const qty = sumField(rows, field.tableField);
    if (qty > 0 && isBlank(configValues?.[field.configKey])) {
      const violation: FieldConfigureHeadersViolation = {
        fieldName: field.tableField,
        section: field.section ?? section,
        message: `${field.label} has a quantity entered but isn't configured. Open Configure Headers and set it.`,
      };
      errors.push({ section: violation.section, message: violation.message });
      violationsByField?.set(field.tableField, violation);
    }
  }
}

// ── Foundations: fields gated by `foundationsPrefixValues` (text-placeholder
// substitution into the column title, e.g. "xxxmm thick recycled 6F2 piling
// mat" → "150mm thick..."). Verified directly against FoundationsNew.tsx —
// every one of these has either no static evaluate code at all
// (`foundationsReferenceMapping.tsx`'s `meshToFloor`/`topLayerMesh`/
// `meshToGarageFloor`: `{}`) or only a placement-only fallback code missing
// the selected concrete/mesh material's own cost (`gen1ConcreteToStrip`/
// `rc35ConcreteToPiled`/`rc35ConcreteToVibro`/`gen1AdditionalConcrete`), so an
// unconfigured field here is a real pricing gap, not just cosmetic.
// `section` stays the take-off page's own tab grouping (set at the
// `checkGatedFields` call site below: Excavate & Concrete / Abnormals /
// Floors) — `label` is what changed here, now verified verbatim against
// FoundationsNew.tsx's own Configure Headers modal JSX (its
// `<Text strong className="... uppercase">` group/field headers), e.g.
// "Piling Mat"/"Concrete Blinding" rather than the previous lowercase
// paraphrases, and "Concrete to Strip/Piled/Vibro" (the modal's own text
// under its "Foundation Types" group) rather than "Concrete to strip/piled/
// vibro foundation".
const FOUNDATIONS_EXCAVATE_CONCRETE_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "pileMat", tableField: "pileMat", label: "Piling Mat" },
  { configKey: "concreteBlinding", tableField: "concreteBlinding", label: "Concrete Blinding" },
  { configKey: "gen1ConcreteToStrip", tableField: "gen1ConcreteToStrip", label: "Concrete to Strip" },
  { configKey: "rc35ConcreteToPiled", tableField: "rc35ConcreteToPiled", label: "Concrete to Piled" },
  { configKey: "rc35ConcreteToVibro", tableField: "rc35ConcreteToVibro", label: "Concrete to Vibro" },
];
const FOUNDATIONS_ABNORMALS_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "gen1AdditionalConcrete", tableField: "gen1AdditionalConcrete", label: "In Additional Concrete" },
];
const FOUNDATIONS_FLOORS_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "meshToFloor", tableField: "meshToFloor", label: "Mesh to Floor" },
  { configKey: "topLayerMesh", tableField: "topLayerMesh", label: "Top Layer Mesh" },
  { configKey: "meshToGarageFloor", tableField: "meshToGarageFloor", label: "Mesh to Garage Floor" },
  { configKey: "bbFloorHouse", tableField: "bbFloorHouse", label: "B&B Floor House" },
  { configKey: "bbFloorGarage", tableField: "bbFloorGarage", label: "B&B Floor Garage" },
];

// ── Foundations — Substructure: fields whose evaluate code is overridden by
// the top-level `substructureBlockType` (Block Type Selection) when a Celcon
// variant is picked (`CELCON_GENERAL_CODE_OVERRIDES` in
// foundationsReferenceMapping.tsx) — the other ~19 Substructure-tab fields
// keep a fixed Thermalite-flavoured code regardless of Block Type, so they're
// not included here.
const FOUNDATIONS_SUBSTRUCTURE_BLOCK_TYPE_FIELDS: readonly string[] = [
  "trench4NBlock300mmWide",
  "trench7NBlock300mmWide",
  "trench4NBlock355mmWide",
  "trench7NBlock355mmWide",
  "aerated7NBlockwork100mm",
  "aerated7NBlockwork140mm",
  "aerated100mmCoursingBrick",
];

/**
 * Public helper for FoundationsBillSummary.tsx's per-row warning icon —
 * every current Foundations Configure Headers violation, keyed by the
 * take-off `fieldName` (identical to `tableField`/`configKey` for every field
 * here — verified against FoundationsBillSummary.tsx's own summary item
 * mapping). Shared by `validateGroundworksConfigureHeaders` below so the two
 * can't drift apart.
 */
export function getFoundationsConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
): Map<string, FieldConfigureHeadersViolation> {
  const map = new Map<string, FieldConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  const foundationsTable: Record<string, any>[] = (gw.foundationsTable || []).filter(isRealRow);
  const discardedErrors: ConfigureHeadersValidationError[] = [];
  checkGatedFields(foundationsTable, gw.foundationsPrefixValues, FOUNDATIONS_EXCAVATE_CONCRETE_GATED_FIELDS, "Foundations — Excavate & Concrete", discardedErrors, map);
  checkGatedFields(foundationsTable, gw.foundationsPrefixValues, FOUNDATIONS_ABNORMALS_GATED_FIELDS, "Foundations — Abnormals", discardedErrors, map);
  checkGatedFields(foundationsTable, gw.foundationsPrefixValues, FOUNDATIONS_FLOORS_GATED_FIELDS, "Foundations — Floors", discardedErrors, map);

  const meshReinforcementQty = sumField(foundationsTable, "meshReinforcement");
  if (meshReinforcementQty > 0 && isBlank(gw.meshReinforcementType)) {
    map.set("meshReinforcement", {
      fieldName: "meshReinforcement",
      section: "Foundations — Excavate & Concrete",
      message: "Mesh Reinforcement has a quantity entered but isn't configured. Open Configure Headers and choose a mesh type under Foundation Types.",
    });
  }

  const substructureBlockTypeQty = FOUNDATIONS_SUBSTRUCTURE_BLOCK_TYPE_FIELDS.reduce(
    (sum, field) => sum + sumField(foundationsTable, field),
    0,
  );
  if (substructureBlockTypeQty > 0 && isBlank(gw.substructureBlockType)) {
    const message = "Substructure blockwork has a quantity entered but Block Type Selection isn't configured. Open Configure Headers on the Substructure tab and choose a block type.";
    FOUNDATIONS_SUBSTRUCTURE_BLOCK_TYPE_FIELDS.forEach((fieldName) => {
      map.set(fieldName, { fieldName, section: "Foundations — Substructure", message });
    });
  }
  return map;
}

// ── Plot Drives: fields gated by `plotDrivesDimensionValues` (text-placeholder
// substitution, same convention). Verified: dataIndex === key === colKey for
// every column; no evaluate-code-badge mechanism is used anywhere in this
// file (addColumnCodes is never passed a dynamicCodes argument).
//
// PlotDrives.tsx's own `columnToDriveTypeMap` groups these 39 columns into 13
// Private/Shared × Macadam/Block/Permeable × Binder/Surface (+ Kerbs &
// Edgings) drive-type sections. Its Configure Headers modal itself is a flat,
// ungrouped list — no tab/section headers of its own — so `label` below is
// each field's own modal text verbatim (`getDimensionFieldsCard`'s `field.title`,
// including its stray double space in "xxmm DB  Base" for col4/col23), even
// though several are identical across different drive types (e.g. col3/col9/
// col22/col28 are all "xxxmm thick MOT Type 1"). Disambiguation instead lives
// entirely in `section`, which carries all three navigation axes the user
// actually needs — stage, domain, drive-type — as "Drives to Binder — Plot
// Drives — <drive type>" / "Drives Surface Course — Plot Drives — <drive
// type>", matching the literal option text of PlotDrives.tsx's own
// `driveStage` selector ("Drives to Binder"/"Drives Surface Course") rather
// than a paraphrase, plus each field's actual `activeTab` drive-type label.
// Note the Shared Block-Paved drive type's tab label itself changes between
// stages in PlotDrives.tsx — "Shared Block Paved Drive" on the binder tab vs.
// "Shared Block Paving" on the surface tab — so col67-69's `section` uses the
// surface wording, not the binder one.
const PLOT_DRIVES_DIMENSION_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "col3", tableField: "col3", label: "xxxmm thick MOT Type 1", section: "Drives to Binder — Plot Drives — Private Macadam Drive" },
  { configKey: "col4", tableField: "col4", label: "xxmm DB  Base", section: "Drives to Binder — Plot Drives — Private Macadam Drive" },
  { configKey: "col5", tableField: "col5", label: "xxmm DB Binder", section: "Drives to Binder — Plot Drives — Private Macadam Drive" },
  { configKey: "col9", tableField: "col9", label: "xxxmm thick MOT Type 1", section: "Drives to Binder — Plot Drives — Private Block Paved Drive" },
  { configKey: "col10", tableField: "col10", label: "xxmm DB Base", section: "Drives to Binder — Plot Drives — Private Block Paved Drive" },
  { configKey: "col11", tableField: "col11", label: "xxmm DB Binder", section: "Drives to Binder — Plot Drives — Private Block Paved Drive" },
  { configKey: "col16", tableField: "col16", label: "xxmm DB binder Running course", section: "Drives to Binder — Plot Drives — Private Permeable Paving" },
  { configKey: "col17", tableField: "col17", label: "xxmm thick 63-10mm Sub Base - Varied Depths", section: "Drives to Binder — Plot Drives — Private Permeable Paving" },
  { configKey: "col18", tableField: "col18", label: "xxmm thick 5-20mm clean aggregate sub base", section: "Drives to Binder — Plot Drives — Private Permeable Paving" },
  { configKey: "col22", tableField: "col22", label: "xxxmm thick MOT Type 1", section: "Drives to Binder — Plot Drives — Shared Macadam Drive" },
  { configKey: "col23", tableField: "col23", label: "xxmm DB  Base", section: "Drives to Binder — Plot Drives — Shared Macadam Drive" },
  { configKey: "col24", tableField: "col24", label: "xxmm DB Binder", section: "Drives to Binder — Plot Drives — Shared Macadam Drive" },
  { configKey: "col28", tableField: "col28", label: "xxxmm thick MOT Type 1", section: "Drives to Binder — Plot Drives — Shared Block Paved Drive" },
  { configKey: "col29", tableField: "col29", label: "xxmm DB Base", section: "Drives to Binder — Plot Drives — Shared Block Paved Drive" },
  { configKey: "col30", tableField: "col30", label: "xxmm DB Binder", section: "Drives to Binder — Plot Drives — Shared Block Paved Drive" },
  { configKey: "col35", tableField: "col35", label: "xxmm DB binder Running course", section: "Drives to Binder — Plot Drives — Shared Permeable Paving" },
  { configKey: "col36", tableField: "col36", label: "xxmm thick 63-10mm Sub Base - Varied Depths", section: "Drives to Binder — Plot Drives — Shared Permeable Paving" },
  { configKey: "col37", tableField: "col37", label: "xxmm thick 5-20mm clean aggregate sub base", section: "Drives to Binder — Plot Drives — Shared Permeable Paving" },
  { configKey: "col50", tableField: "col50", label: "xxmm DB Surface course", section: "Drives Surface Course — Plot Drives — Private Macadam Drive" },
  { configKey: "col51", tableField: "col51", label: "xxmm HRA Surface course", section: "Drives Surface Course — Plot Drives — Private Macadam Drive" },
  { configKey: "col52", tableField: "col52", label: "xxmm SMA Surface course", section: "Drives Surface Course — Plot Drives — Private Macadam Drive" },
  { configKey: "col55", tableField: "col55", label: "xxmm sand Laying Course", section: "Drives Surface Course — Plot Drives — Private Block Paved Drive" },
  { configKey: "col59", tableField: "col59", label: "xxmm thick 2-6mm laying Aggregate", section: "Drives Surface Course — Plot Drives — Private Permeable Paving" },
  { configKey: "col63", tableField: "col63", label: "xxmm DB Surface course", section: "Drives Surface Course — Plot Drives — Shared Macadam Drive" },
  { configKey: "col64", tableField: "col64", label: "xxmm HRA Surface course", section: "Drives Surface Course — Plot Drives — Shared Macadam Drive" },
  { configKey: "col65", tableField: "col65", label: "xxmm SMA Surface course", section: "Drives Surface Course — Plot Drives — Shared Macadam Drive" },
  { configKey: "col67", tableField: "col67", label: "xxmm sand Laying Course", section: "Drives Surface Course — Plot Drives — Shared Block Paving" },
  { configKey: "col71", tableField: "col71", label: "xxmm thick 2-6mm laying Aggregate", section: "Drives Surface Course — Plot Drives — Shared Permeable Paving" },
  // Block/Permeable Paving surface course brand selections — title text is
  // fixed ("60mm Concrete paviors"/"80mm Concrete paviors", no "xx"
  // placeholder), but the resource code still comes entirely from
  // dimensionValues[colKey] (a brand-keyword-matched code like GWALPD002),
  // so an unset value leaves the row genuinely uncoded, same failure mode.
  { configKey: "col56", tableField: "col56", label: "60mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Private Block Paved Drive" },
  { configKey: "col57", tableField: "col57", label: "80mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Private Block Paved Drive" },
  { configKey: "col60", tableField: "col60", label: "60mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Private Permeable Paving" },
  { configKey: "col61", tableField: "col61", label: "80mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Private Permeable Paving" },
  { configKey: "col68", tableField: "col68", label: "60mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Shared Block Paving" },
  { configKey: "col69", tableField: "col69", label: "80mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Shared Block Paving" },
  { configKey: "col72", tableField: "col72", label: "60mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Shared Permeable Paving" },
  { configKey: "col73", tableField: "col73", label: "80mm Concrete paviors", section: "Drives Surface Course — Plot Drives — Shared Permeable Paving" },
];
// Plot Drives' three kerb columns are gated by `columnHeaders`, not
// `dimensionValues` — a separate config object, same domain. All three live
// on the "Kerbs and Edgings" tab (key `kerbsAndEdgings`), which only exists
// under the "Drives to Binder" stage (absent from the surface tab set), so
// that stage prefix is included even though there's no surface-stage
// counterpart to disambiguate from. They're titled identically "xxxx Kerb"
// inside the modal itself (verified against the field's own dimension-field
// entry) — the modal has no way to tell them apart either, so the ordinal
// stays in `label` (not `section`, which is left as the plain tab name)
// purely so the 3 error lines don't look like duplicates.
const PLOT_DRIVES_HEADER_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "col43", tableField: "col43", label: "Kerb 1 (xxxx Kerb)", section: "Drives to Binder — Plot Drives — Kerbs and Edgings" },
  { configKey: "col44", tableField: "col44", label: "Kerb 2 (xxxx Kerb)", section: "Drives to Binder — Plot Drives — Kerbs and Edgings" },
  { configKey: "col45", tableField: "col45", label: "Kerb 3 (xxxx Kerb)", section: "Drives to Binder — Plot Drives — Kerbs and Edgings" },
];

export interface PlotDrivesConfigureHeadersViolation {
  /** e.g. "col50" — the raw Configure Headers config key. */
  colKey: string;
  /** Semantic field name (PLOT_DRIVES_COLUMN_MAPPING[colKey]), e.g. "macadamDbSurfaceCourse" — how PlotDrivesBillSummary.tsx's summary rows identify themselves. */
  fieldName: string;
  label: string;
  section: string;
  message: string;
}

/**
 * Shared core of the Plot Drives Configure Headers check — realigns/sanitizes
 * the raw take-off data the same way `validateGroundworksConfigureHeaders`
 * does below, then flags every gated field with a quantity but no config.
 * Returns violations keyed by both `colKey` and its semantic `fieldName` so
 * callers can match against either raw table columns or a Bill Summary row's
 * `fieldName` (see `getPlotDrivesConfigureHeadersViolationsByField`, used by
 * PlotDrivesBillSummary.tsx's per-row warning icon).
 */
function computePlotDrivesConfigureHeadersViolations(
  gw: Record<string, any>,
): PlotDrivesConfigureHeadersViolation[] {
  const violations: PlotDrivesConfigureHeadersViolation[] = [];
  const { rows: plotDrivesTableAligned, columnHeaders: plotDrivesColumnHeadersAligned } =
    alignPlotDrivesSectionBoundaries(gw.plotDrivesColumnHeaders || {}, gw.plotDrivesTable || []);
  const { dimensionValues: plotDrivesDimensionValuesSanitized, columnHeaders: plotDrivesColumnHeadersSanitized } =
    sanitizePlotDrivesConfig(gw.plotDrivesDimensionValues || {}, plotDrivesColumnHeadersAligned);
  const plotDrivesTable: Record<string, any>[] = plotDrivesTableAligned.filter(isRealRow);

  // Fields whose evaluate code IS the dimension value itself (PLOT_DRIVES_COL_CODES
  // entries marked `{}`/"varies" in plotDrivesReferenceMapping.tsx) — DB Base/Binder
  // (col4/5/10/11/23/24/29/30) and the 14 surface-course/paviors columns. Pricing
  // (createPlotDrivesRateLookupMap → getDynamicCodeInfo) only finds a rate when the
  // value resolves to a real ALP/GWALP... code or an exact EVALUATE_CODES label
  // match — a non-blank value that fails to resolve (e.g. raw imported header text
  // with no matching code/brand) is just as unpriced as a blank one, so plain
  // isBlank() under-reports it: the row can show a real quantity in the Bill
  // Summary yet never get flagged. Every other gated field stores its config as a
  // literal thickness/text value (not a code), where isBlank() is the right test.
  const PLOT_DRIVES_DYNAMIC_CODE_COLS = new Set<string>([
    "col4", "col5", "col10", "col11", "col23", "col24", "col29", "col30",
    "col50", "col51", "col52", "col56", "col57", "col60", "col61",
    "col63", "col64", "col65", "col68", "col69", "col72", "col73",
  ]);
  const isFieldUnconfigured = (configKey: string, value: string | undefined): boolean =>
    PLOT_DRIVES_DYNAMIC_CODE_COLS.has(configKey) ? !isResolvablePlotDrivesEvaluateValue(value) : isBlank(value);

  const collect = (
    configValues: Record<string, string> | undefined,
    fields: readonly GatedField[],
  ): void => {
    for (const field of fields) {
      const qty = sumField(plotDrivesTable, field.tableField);
      if (qty > 0 && isFieldUnconfigured(field.configKey, configValues?.[field.configKey])) {
        violations.push({
          colKey: field.configKey,
          fieldName: PLOT_DRIVES_COLUMN_MAPPING[field.configKey] ?? field.configKey,
          label: field.label,
          section: field.section ?? "Plot Drives",
          message: `${field.label} has a quantity entered but isn't configured. Open Configure Headers and set it.`,
        });
      }
    }
  };
  collect(plotDrivesDimensionValuesSanitized, PLOT_DRIVES_DIMENSION_GATED_FIELDS);
  collect(plotDrivesColumnHeadersSanitized, PLOT_DRIVES_HEADER_GATED_FIELDS);
  return violations;
}

/**
 * Public helper for PlotDrivesBillSummary.tsx: every current Plot Drives
 * Configure Headers violation, keyed by the semantic `fieldName` its summary
 * rows already carry — lets the Bill Summary show a warning icon directly on
 * the affected line item instead of only surfacing this in the Send for
 * Review gate on /groundworks/review.
 */
export function getPlotDrivesConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
): Map<string, PlotDrivesConfigureHeadersViolation> {
  const map = new Map<string, PlotDrivesConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  computePlotDrivesConfigureHeadersViolations(gw).forEach((v) => map.set(v.fieldName, v));
  return map;
}

// ── Plot Pavings: fields gated by `plotPavingsDimensionValues` (text
// substitution for the two "SubBase" fields, evaluate-code dropdown choice
// stored the same way for the rest — same blank/unset test either way).
// PlotPavings.tsx has 2 tabs — "Paving Paths" (pavingSubBase,
// pavingPlainGreyPavings) and "Macadam Paths" (the 5 macadam* fields). Its
// Configure Headers modal is a flat, ungrouped list (no internal sections),
// so `label` is each field's exact modal text and `section` (the tab to open
// first) carries the only disambiguation.
const PLOT_PAVINGS_DIMENSION_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "pavingSubBase", tableField: "pavingSubBase", label: "xxxmm thick Stone bed (m²)", section: "Plot Pavings — Paving Paths" },
  { configKey: "macadamSubBase", tableField: "macadamSubBase", label: "xxxmm thick MOT Type 1 (m²)", section: "Plot Pavings — Macadam Paths" },
  { configKey: "macadamDbBase", tableField: "macadamDbBase", label: "xxmm DB Base (m²)", section: "Plot Pavings — Macadam Paths" },
  { configKey: "macadamDbBinder", tableField: "macadamDbBinder", label: "xxmm DB Binder (m²)", section: "Plot Pavings — Macadam Paths" },
  { configKey: "macadamDbSurfaceCourse", tableField: "macadamDbSurfaceCourse", label: "xxmm DB Surface course (m²)", section: "Plot Pavings — Macadam Paths" },
  { configKey: "macadamHraSurfaceCourse", tableField: "macadamHraSurfaceCourse", label: "xxmm HRA Surface course (m²)", section: "Plot Pavings — Macadam Paths" },
  { configKey: "macadamSmaSurfaceCourse", tableField: "macadamSmaSurfaceCourse", label: "xxmm SMA Surface course (m²)", section: "Plot Pavings — Macadam Paths" },
  { configKey: "pavingPlainGreyPavings", tableField: "pavingPlainGreyPavings", label: "Slabs (m²)", section: "Plot Pavings — Paving Paths" },
];

/** Public helper for PlotPavingsBillSummary.tsx's per-row warning icon — see getFoundationsConfigureHeadersViolationsByField's doc comment for the convention. */
export function getPlotPavingsConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
): Map<string, FieldConfigureHeadersViolation> {
  const map = new Map<string, FieldConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  const plotPavingsTable: Record<string, any>[] = (gw.plotPavingsTable || []).filter(isRealRow);
  checkGatedFields(plotPavingsTable, gw.plotPavingsDimensionValues, PLOT_PAVINGS_DIMENSION_GATED_FIELDS, "Plot Pavings", [], map);
  return map;
}

// ── Scaffold: fields gated by `scaffoldDimensionValues` (text substitution).
// Scaffold.tsx has 3 tabs — "Scaffold Platforms", "Loading Bays", "Stair
// Access" — and its Configure Headers modal is likewise flat/ungrouped, with
// the crush6F2/Type1 pair's exact modal text identical across all 3 tabs;
// `section` is the only thing that tells them apart.
const SCAFFOLD_DIMENSION_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "scaffoldCrush6F2", tableField: "scaffoldCrush6F2", label: "xxxmm crush 6F2 (m²)", section: "Scaffold — Scaffold Platforms" },
  { configKey: "scaffoldType1", tableField: "scaffoldType1", label: "xxmm Type1 (m²)", section: "Scaffold — Scaffold Platforms" },
  { configKey: "loadingCrush6F2", tableField: "loadingCrush6F2", label: "xxxmm crush 6F2 (m²)", section: "Scaffold — Loading Bays" },
  { configKey: "loadingType1", tableField: "loadingType1", label: "xxmm Type1 (m²)", section: "Scaffold — Loading Bays" },
  { configKey: "stairCrush6F2", tableField: "stairCrush6F2", label: "xxxmm crush 6F2 (m²)", section: "Scaffold — Stair Access" },
  { configKey: "stairType1", tableField: "stairType1", label: "xxmm Type1 (m²)", section: "Scaffold — Stair Access" },
];

/**
 * Public helper for ScaffoldBillSummary.tsx's per-row warning icon. Unlike
 * every other domain here, Scaffold's Bill Summary never shows the 6 raw
 * gated fields (scaffoldCrush6F2/scaffoldType1/etc.) as their own rows — it
 * consolidates each of the 3 tabs' Level&Compact+Geotextile+Crush6F2+Type1
 * fields into ONE displayed line item, keyed by that tab's own quantity
 * field (`scaffoldPlatform`/`loadingBay4x2m`/`stairAccess3x3m` —
 * `CONSOLIDATE_FIELDS` in ScaffoldBillSummary.tsx). So a raw-field violation
 * is re-keyed onto its consolidated row here, and the Loading Bay/Stair
 * Access "size" group violations (which have no raw gated field of their own)
 * are added directly under the same consolidated keys.
 */
export function getScaffoldConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
): Map<string, FieldConfigureHeadersViolation> {
  const map = new Map<string, FieldConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  const scaffoldTable: Record<string, any>[] = (gw.scaffoldTable || []).filter(isRealRow);
  checkGatedFields(scaffoldTable, gw.scaffoldDimensionValues, SCAFFOLD_DIMENSION_GATED_FIELDS, "Scaffold", [], map);

  const remapToConsolidatedField = (crushKey: string, type1Key: string, consolidatedFieldName: string): void => {
    const violation = map.get(crushKey) ?? map.get(type1Key);
    if (violation) map.set(consolidatedFieldName, { ...violation, fieldName: consolidatedFieldName });
  };
  remapToConsolidatedField("scaffoldCrush6F2", "scaffoldType1", "scaffoldPlatform");
  remapToConsolidatedField("loadingCrush6F2", "loadingType1", "loadingBay4x2m");
  remapToConsolidatedField("stairCrush6F2", "stairType1", "stairAccess3x3m");

  // Loading Bay / Stair Access "size" (e.g. "4.5x2.5m") is a section-label
  // field, not itself resource-code-gated — but its group's quantities
  // shouldn't be submitted with the section left unsized either.
  const scaffoldDimensionValues: Record<string, any> = gw.scaffoldDimensionValues || {};
  const loadingBayQty = sumField(scaffoldTable, "loadingCrush6F2") + sumField(scaffoldTable, "loadingType1");
  if (loadingBayQty > 0 && isBlank(scaffoldDimensionValues.loadingBaySize)) {
    map.set("loadingBay4x2m", {
      fieldName: "loadingBay4x2m",
      section: "Scaffold — Loading Bays",
      message: "Loading bay has a quantity entered but its size isn't configured. Open Configure Headers and set the Loading bay size.",
    });
  }
  const stairAccessQty = sumField(scaffoldTable, "stairCrush6F2") + sumField(scaffoldTable, "stairType1");
  if (stairAccessQty > 0 && isBlank(scaffoldDimensionValues.stairAccessSize)) {
    map.set("stairAccess3x3m", {
      fieldName: "stairAccess3x3m",
      section: "Scaffold — Stair Access",
      message: "Stair Access has a quantity entered but its size isn't configured. Open Configure Headers and set the Stair Access size.",
    });
  }
  return map;
}

// ── Garden: fields gated by `gardenDimensionValues` (text substitution).
const GARDEN_DIMENSION_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "thickNoDigLayer", tableField: "thickNoDigLayer", label: "xxxmm thick 'no dig' layer" },
  { configKey: "spreadLevelCleanSubsoilCapSiteWon", tableField: "spreadLevelCleanSubsoilCapSiteWon", label: "Spread and level xxxmm thick site won clean subsoil capping layer" },
  { configKey: "spreadLevelCleanSubsoilCapImported", tableField: "spreadLevelCleanSubsoilCapImported", label: "Spread and level xxxmm thick imported clean subsoil capping layer" },
  { configKey: "spreadLevelTopsoilSiteWon", tableField: "spreadLevelTopsoilSiteWon", label: "Spread and level xxxmm thick site won topsoil" },
  { configKey: "spreadLevelTopsoilImported", tableField: "spreadLevelTopsoilImported", label: "Spread and level xxxmm thick imported topsoil" },
];

/** Public helper for GardenBillSummary.tsx's per-row warning icon — see getFoundationsConfigureHeadersViolationsByField's doc comment for the convention. */
export function getGardenConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
): Map<string, FieldConfigureHeadersViolation> {
  const map = new Map<string, FieldConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  const gardenTable: Record<string, any>[] = (gw.gardenTable || []).filter(isRealRow);
  checkGatedFields(gardenTable, gw.gardenDimensionValues, GARDEN_DIMENSION_GATED_FIELDS, "Garden", [], map);
  return map;
}

// ── Drainage: 4 fields gated by `drainageDimensionValues` (an evaluate-code
// dropdown, not text substitution — the code the user picks becomes the
// resource code directly, so an unset value has literally no code at all).
// All 4 are Storm-only fields, gated inside StandardDrainageView.tsx's own
// "Configure Storm Headers" modal, whose two field labels ("Drainage Channel
// (m)"/"Land Drain (m)") are shared by OSMA and Polypipe alike (the modal
// picks which config key to write via its own supplier selector, not a
// supplier-qualified label) — the OSMA/Polypipe prefix on `label` below is
// therefore ours, added only so the two suppliers' otherwise-identical error
// lines don't look like duplicates; `section` stays the take-off tab grouping.
const DRAINAGE_DIMENSION_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "osmaStormDrainageChannel", tableField: "osmaStormDrainageChannel", label: "OSMA — Drainage Channel (m)", section: "Drainage — Storm" },
  { configKey: "osmaStormLandDrain", tableField: "osmaStormLandDrain", label: "OSMA — Land Drain (m)", section: "Drainage — Storm" },
  { configKey: "polypipeStormDrainageChannel", tableField: "polypipeStormDrainageChannel", label: "Polypipe — Drainage Channel (m)", section: "Drainage — Storm" },
  { configKey: "polypipeStormLandDrain", tableField: "polypipeStormLandDrain", label: "Polypipe — Land Drain (m)", section: "Drainage — Storm" },
];

/**
 * Public helper for DrainageBillSummary.tsx's per-row warning icon. The OSMA/
 * Polypipe "Nr Plots Take Off"/"Total Plots" group checks have no single
 * gated field of their own — DrainageBillSummary.tsx's `buildPerPlotRow`
 * renders one combined row per supplier×domain, identified by the first of
 * that group's own combined fields (`"osma32mmBend"`/`"osmaStorm32mmBend"`/
 * `"polypipe32mmBend"`/`"polypipeStorm32mmBend"` — verified against that
 * function's own call sites), so the violation is keyed there directly.
 */
export function getDrainageConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
): Map<string, FieldConfigureHeadersViolation> {
  const map = new Map<string, FieldConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  const drainageTable: Record<string, any>[] = (gw.drainageTableStandard || gw.drainageTable || []).filter(isRealRow);
  checkGatedFields(drainageTable, gw.drainageDimensionValues, DRAINAGE_DIMENSION_GATED_FIELDS, "Drainage", [], map);

  const osmaConfig: Record<string, any> = gw.drainageDimensionValues || {};
  const osmaCombinedQty =
    OSMA_FOUL_COMBINED_FIELDS.reduce((s, f) => s + sumField(drainageTable, f), 0) +
    OSMA_STORM_COMBINED_FIELDS.reduce((s, f) => s + sumField(drainageTable, f), 0);
  const osmaNrPlotsTakeOff = Number(osmaConfig.foulNrPlotsTakeOff) || 0;
  const osmaTotalPlots = Number(osmaConfig.foulTotalPlots) || 0;
  if (osmaCombinedQty > 0 && (osmaNrPlotsTakeOff <= 0 || osmaTotalPlots <= 0)) {
    const message = "OSMA pipework/fittings have quantities entered but \"Nr Plots Take off\"/\"Total Plots\" aren't configured — this zeroes the rate for the whole OSMA group. Open Configure Headers and set both values.";
    map.set("osma32mmBend", { fieldName: "osma32mmBend", section: "Drainage — OSMA (Foul & Storm)", message });
    map.set("osmaStorm32mmBend", { fieldName: "osmaStorm32mmBend", section: "Drainage — OSMA (Foul & Storm)", message });
  }
  const polypipeConfig: Record<string, any> = gw.drainagePolypipeConfig || {};
  const polypipeCombinedQty =
    POLYPIPE_FOUL_COMBINED_FIELDS.reduce((s, f) => s + sumField(drainageTable, f), 0) +
    POLYPIPE_STORM_COMBINED_FIELDS.reduce((s, f) => s + sumField(drainageTable, f), 0);
  const polypipeNrPlotsTakeOff = Number(polypipeConfig.nrPlotsTakeOff) || 0;
  const polypipeTotalPlots = Number(polypipeConfig.totalPlots) || 0;
  if (polypipeCombinedQty > 0 && (polypipeNrPlotsTakeOff <= 0 || polypipeTotalPlots <= 0)) {
    const message = "Polypipe pipework/fittings have quantities entered but \"Nr Plots Take off\"/\"Total Plots\" aren't configured — this zeroes the rate for the whole Polypipe group. Open Configure Headers and set both values.";
    map.set("polypipe32mmBend", { fieldName: "polypipe32mmBend", section: "Drainage — Polypipe (Foul & Storm)", message });
    map.set("polypipeStorm32mmBend", { fieldName: "polypipeStorm32mmBend", section: "Drainage — Polypipe (Foul & Storm)", message });
  }
  return map;
}

// ── Geo/Tobermore Wall: 8 TPCW columns gated by
// `geoToberWallDimensionValues` — unlike Retaining Walls, there is no default
// fallback here; an unset column literally keeps the placeholder title
// "TPCW xxxxmm" and GEOToberWallBillSummary.tsx can't resolve a price for it.
// All 8 TPCW columns live on the "Tobermore Walls" take-off tab (activeTab
// === "tobermore") — `section` stays that tab name. The field opens via its
// own "Configure TPCW Column Headers" modal, which groups every field under
// one `ConfigSection title="TPCW Columns"` and labels each `ConfigField`
// "TPCW Column 1".."TPCW Column 8" — `label` below now matches that modal
// text verbatim instead of the previous "TPCW 1".."TPCW 8".
const GEO_TOBER_WALL_DIMENSION_GATED_FIELDS: readonly GatedField[] = [
  { configKey: "tpcw1", tableField: "tpcw1", label: "TPCW Column 1", section: "Geo/Tobermore Wall — Tobermore Walls" },
  { configKey: "tpcw2", tableField: "tpcw2", label: "TPCW Column 2", section: "Geo/Tobermore Wall — Tobermore Walls" },
  { configKey: "tpcw3", tableField: "tpcw3", label: "TPCW Column 3", section: "Geo/Tobermore Wall — Tobermore Walls" },
  { configKey: "tpcw4", tableField: "tpcw4", label: "TPCW Column 4", section: "Geo/Tobermore Wall — Tobermore Walls" },
  { configKey: "tpcw5", tableField: "tpcw5", label: "TPCW Column 5", section: "Geo/Tobermore Wall — Tobermore Walls" },
  { configKey: "tpcw6", tableField: "tpcw6", label: "TPCW Column 6", section: "Geo/Tobermore Wall — Tobermore Walls" },
  { configKey: "tpcw7", tableField: "tpcw7", label: "TPCW Column 7", section: "Geo/Tobermore Wall — Tobermore Walls" },
  { configKey: "tpcw8", tableField: "tpcw8", label: "TPCW Column 8", section: "Geo/Tobermore Wall — Tobermore Walls" },
];

/**
 * Public helper for GEOToberWallBillSummary.tsx's per-row warning icon —
 * keyed by the raw `tpcw#` field name. That component's own Collective-view
 * row is keyed by `spec.id` (e.g. `"tobermore_tpcw1"`), not `tpcw#` directly —
 * it already has its own `specIdToField` inverse of `geoToberWallFieldToSpecId`
 * to translate, so this returns the plain `tpcw#`-keyed map rather than
 * duplicating that translation here.
 */
export function getGeoToberWallConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
): Map<string, FieldConfigureHeadersViolation> {
  const map = new Map<string, FieldConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  const geoToberWallTable: Record<string, any>[] = (gw.geoToberWallTable || []).filter(isRealRow);
  checkGatedFields(geoToberWallTable, gw.geoToberWallDimensionValues, GEO_TOBER_WALL_DIMENSION_GATED_FIELDS, "Geo/Tobermore Wall", [], map);
  return map;
}

// ── Cut & Fill: Site Clearance (topsoilStrip, a plain gated field) and Soil
// Management (Topsoil/Subsoil Management — derived quantities, not simple
// take-off table fields, so not expressible as GatedField entries).
/**
 * Public helper for GroundworksCutFillBillSummary.tsx's per-row warning icon.
 * Topsoil Management's row is keyed by a *computed* fieldName, not a fixed
 * string — `topsoilFieldName` mirrors GroundworksCutFillBillSummary.tsx's own
 * rule exactly (and `computeSoilManagementSection`'s, `groundworksBoqSummaryComputer.ts`)
 * so this attaches to the same row the Bill Summary actually renders; Subsoil
 * Management's row is always the fixed key `"soil-subsoil-line"`.
 */
export function getCutFillConfigureHeadersViolationsByField(
  groundworksData: GroundworksData | undefined,
  retainingWallsBuildUpSpecs?: RetainingWallsBuildUpSpecification[],
): Map<string, FieldConfigureHeadersViolation> {
  const map = new Map<string, FieldConfigureHeadersViolation>();
  if (!groundworksData) return map;
  const gw = groundworksData as unknown as Record<string, any>;
  const cutFillsTable: Record<string, any>[] = (gw.cutFillsTable || []).filter(isRealRow);

  const topsoilStripQty = sumField(cutFillsTable, "topsoilStrip");
  if (topsoilStripQty > 0 && isBlank(gw.cutFillsPrefixValues?.topsoilStrip)) {
    map.set("topsoilStrip", {
      fieldName: "topsoilStrip",
      section: "Cut & Fill — Site Clearance",
      message: "Strip topsoil has a quantity entered but its depth isn't configured. Open Configure Headers on the Site Clearance tab and set the strip depth.",
    });
  }

  const totalOnsite = sumField(cutFillsTable, "topsoilDisposalOnsite");
  const totalOffsite = sumField(cutFillsTable, "topsoilDisposalOffsite");
  const topsoilQty = Math.abs(totalOnsite - totalOffsite);
  const topsoilMgmt = gw.topsoilManagement;
  const topsoilCode = String(topsoilMgmt?.code || "").trim().toUpperCase();
  if (topsoilQty > 0 && !topsoilCode) {
    const topsoilFieldName = (topsoilMgmt?.management === "Surplus" || !topsoilMgmt?.management)
      ? "topsoilDisposalOffsite"
      : "topsoilDisposalOnsite";
    map.set(topsoilFieldName, {
      fieldName: topsoilFieldName,
      section: "Cut & Fill — Soil Management",
      message: "Topsoil Management has a quantity but hasn't been configured. Open Configure Headers on the Soil Management tab and choose a Surplus/Deficit option (or explicitly exclude it).",
    });
  }

  const soilManagementData = gw.soilManagementData || {};
  const derivedSoilValues = computeSoilManagementDerivedCutFillValues(gw, retainingWallsBuildUpSpecs);
  const computedSoilData: Record<string, number> = { ...soilManagementData, ...derivedSoilValues };
  const soilRows = [
    { cutField: "foundationsCutArisings", fillField: "foundationsFill" },
    { cutField: "slabPathsCut", fillField: "slabPathsFill" },
    { cutField: "gardensCut", fillField: "gardensFill" },
    { cutField: "drivesCut", fillField: "drivesFill" },
    { cutField: "foundationsArisingsCut", fillField: "foundationsArisingsFill" },
    { cutField: "plotDrainageCut", fillField: "plotDrainageFill" },
    { cutField: "wallsCut", fillField: "wallsFill" },
    { cutField: "plotServicesCut", fillField: "plotServicesFill" },
  ];
  const subsoilTotalCut = soilRows.reduce((sum, r) => sum + (Number(computedSoilData?.[r.cutField]) || 0), 0);
  const subsoilTotalFill = soilRows.reduce((sum, r) => sum + (Number(computedSoilData?.[r.fillField]) || 0), 0);
  const subsoilQty = Math.abs(subsoilTotalCut - subsoilTotalFill);
  const subsoilCode = String(gw.subsoilManagement?.code || "").trim().toUpperCase();
  if (subsoilQty > 0 && !subsoilCode) {
    map.set("soil-subsoil-line", {
      fieldName: "soil-subsoil-line",
      section: "Cut & Fill — Soil Management",
      message: "Subsoil Management has a quantity but hasn't been configured. Open Configure Headers on the Soil Management tab and choose a Surplus/Deficit option (or explicitly exclude it).",
    });
  }
  return map;
}

/**
 * Checks Groundworks take-off data for a quantity entered against a
 * "Configure Headers" field that hasn't actually been configured — meaning
 * that quantity can never resolve to a price (its description/resource
 * code still depends on a value the Configure Headers modal never set).
 * Intended to run before "Send for Review" on `/groundworks/review`, and
 * block submission while `errors` is non-empty.
 *
 * Every check here was verified directly against the domain's own take-off
 * page and/or Bill Summary component (not inferred by pattern-matching
 * across pages, which turned out unreliable — see below), so this can't
 * silently drift out of sync with what actually shows as unpriced:
 *  - **Cut & Fill → Site Clearance**: strip topsoil depth, gated by
 *    `cutFillsPrefixValues.topsoilStrip`.
 *  - **Cut & Fill → Soil Management**: Topsoil/Subsoil Management, gated by
 *    `topsoilManagement.code` / `subsoilManagement.code`. A code of `'NONE'`
 *    is a deliberate "excluded from this tender" choice, not "unconfigured".
 *  - **Foundations**: 11 fields gated by `foundationsPrefixValues` (piling
 *    mat, concrete blinding, 3 concrete-grade fields, additional concrete,
 *    3 mesh fields, and B&B Floor House/Garage) plus **Mesh Reinforcement**,
 *    gated by the top-level `meshReinforcementType`, plus **Substructure
 *    Block Type Selection**, gated by the top-level `substructureBlockType`
 *    (checked against the 7 specific blockwork fields whose code it actually
 *    overrides, not the ~19 other Substructure-tab fields that keep a fixed
 *    code regardless — see `FOUNDATIONS_SUBSTRUCTURE_BLOCK_TYPE_FIELDS`).
 *    This is every field the Foundations Configure Headers modal exposes
 *    except the Terram checkbox, which only adds an extra code on top of an
 *    already-priced Piling Mat rather than gating one of its own.
 *  - **Drainage**: the 4 Drainage Channel/Land Drain fields (OSMA + Polypipe,
 *    Storm only) gated by `drainageDimensionValues`, plus the OSMA and
 *    Polypipe "Nr Plots Take Off"/"Total Plots" divisor pairs — either being
 *    unset zeroes the rate for their *entire* combined-fields group (every
 *    pipe/fitting/PPIC/etc. quantity for that supplier), not just one field.
 *  - **Plot Drives**: 36 fields gated by `plotDrivesDimensionValues`
 *    (including the 8 Block/Permeable Paving surface course brand
 *    selections — fixed "60mm/80mm Concrete paviors" title text, but still
 *    resource-code-gated), plus 3 kerb columns gated by
 *    `plotDrivesColumnHeaders` instead.
 *  - **Plot Pavings**: 8 fields gated by `plotPavingsDimensionValues`.
 *  - **Scaffold**: 6 fields gated by `scaffoldDimensionValues`, plus Loading
 *    Bay size and Stair Access size — each a section-label field (not itself
 *    resource-code-gated) required whenever its group has a quantity.
 *  - **Garden**: 5 fields gated by `gardenDimensionValues`.
 *  - **Geo/Tobermore Wall**: the 8 TPCW columns gated by
 *    `geoToberWallDimensionValues`.
 *
 * Deliberately NOT covered:
 *  - **Foundations' Terram checkbox**: it only adds an extra code on top of
 *    Piling Mat's already-resolved code (`ALP031A+GWALP004` vs `GWALP004`) —
 *    Piling Mat itself is priced either way, so there's no "unpriced" state
 *    tied to Terram specifically to catch.
 *  - **Plot Service**: its only Configure Headers field
 *    (`plotServiceConfig.plotServicesApartmentsMultiple`) is a pricing
 *    multiplier that always has a valid selected value — no reachable
 *    "unconfigured" state exists to check.
 *  - **Retaining Walls**: every configurable field
 *    (`wallsBuildUpConfig.screenWallsLabel`/`.eoPillarLabel`/
 *    `.facingBrickworkLabel`) is read with a non-blank default fallback
 *    (`SCREEN_WALLS_FREE_ISSUE_LABEL`, etc.), so it always resolves a valid
 *    code whether or not Configure Headers was ever opened — there is no
 *    "quantity but unpriced" state to catch here either.
 */
export function validateGroundworksConfigureHeaders(
  groundworksData: GroundworksData | undefined,
  retainingWallsBuildUpSpecs?: RetainingWallsBuildUpSpecification[],
): ConfigureHeadersValidationResult {
  const errors: ConfigureHeadersValidationError[] = [];
  if (!groundworksData) return { isValid: true, errors };

  // Every domain below now runs through the same exported `getXConfigureHeadersViolationsByField`
  // functions the corresponding Bill Summary uses for its own per-row warning icon (see each
  // function's own doc comment), so the Send for Review gate here and the Bill Summary icons can
  // never drift apart. Scaffold's map holds both a raw gated field (e.g. "scaffoldCrush6F2") and
  // its remapped consolidated-row key (e.g. "scaffoldPlatform") for the SAME violation, and
  // Foundations' Substructure Block Type check sets one entry per affected field — both would
  // duplicate the same message if pushed as-is, so `seenMessages` dedupes by section+message
  // across every domain before this becomes the flat `errors` list Modal.confirm renders.
  const seenMessages = new Set<string>();
  const pushUnique = (violations: Iterable<FieldConfigureHeadersViolation>): void => {
    for (const v of violations) {
      const dedupeKey = `${v.section}␟${v.message}`;
      if (seenMessages.has(dedupeKey)) continue;
      seenMessages.add(dedupeKey);
      errors.push({ section: v.section, message: v.message });
    }
  };

  pushUnique(getCutFillConfigureHeadersViolationsByField(groundworksData, retainingWallsBuildUpSpecs).values());
  pushUnique(getFoundationsConfigureHeadersViolationsByField(groundworksData).values());
  pushUnique(getDrainageConfigureHeadersViolationsByField(groundworksData).values());
  pushUnique(computePlotDrivesConfigureHeadersViolations(groundworksData as unknown as Record<string, any>));
  pushUnique(getPlotPavingsConfigureHeadersViolationsByField(groundworksData).values());
  pushUnique(getScaffoldConfigureHeadersViolationsByField(groundworksData).values());
  pushUnique(getGardenConfigureHeadersViolationsByField(groundworksData).values());
  pushUnique(getGeoToberWallConfigureHeadersViolationsByField(groundworksData).values());

  return { isValid: errors.length === 0, errors };
}
